import { ReportGeneratorStatus } from "@/constants/ReportGeneratorStatus.enum";
import { ReportType } from "@/constants/ReportType.enums";
import { token } from "@/theme";
import React from 'react';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from "dayjs";
import ReportStore from "@/store/Report/ReportStore";

export const getBackGroundColorBasedOnStatus = (status: ReportGeneratorStatus): string => {
    switch (status) {
      case ReportGeneratorStatus.Published:
        return token.colorSuccess;
      case ReportGeneratorStatus.Failed:
        return token.colorError;
      case ReportGeneratorStatus.InProgress:
        return token.colorPrimary;
      case ReportGeneratorStatus.InDraft:
        return token.colorTextSecondary;
      case ReportGeneratorStatus.SentForPublishing:
        return token.colorWarning;
      default:
        return null;
    }
};

const InDraftText = ({ t }: { t: (key: string) => string }) => {
  const translatedText = t('inDraft');
  
  const bracketMatch = translatedText.match(/\(([^)]+)\)/);
  
  if (bracketMatch) {
    const beforeBracket = translatedText.substring(0, translatedText.indexOf('('));
    const insideBracket = bracketMatch[1];
    const afterBracket = translatedText.substring(translatedText.indexOf(')') + 1);
    
    return (
      <span>
        {beforeBracket}
        <span style={{ color: token.colorTextSecondary }}>({insideBracket})</span>
        {afterBracket}
      </span>
    );
  }
  
  return <span>{translatedText}</span>;
};

export const getTextBasedOnStatus = (
  status: ReportGeneratorStatus, 
  t: (key: string) => string
): string | React.ReactElement => {
    switch (status) {
      case ReportGeneratorStatus.Published:
        return t('published');
      case ReportGeneratorStatus.Failed:
        return t('failed');
      case ReportGeneratorStatus.InProgress:
        return t('inProgress');
      case ReportGeneratorStatus.InDraft:
        return <InDraftText t={t} />;
      case ReportGeneratorStatus.SentForPublishing:
        return t('sentForPublishing');
      default:
        return t('');
    }
};

export const getActionsMenuItemsBasedOnStatus = (status: ReportGeneratorStatus, t: (key: string) => string) => {
  switch (status) {
    case ReportGeneratorStatus.InDraft:
      return [
        { key: 'Upload', label: t('Upload'), icon: <UploadOutlined /> },
        { key: 'Delete', label: t('Delete'), icon: <DeleteOutlined /> }
      ];
  }
  return [];
};

export const getPublishEditMenuBasedOnStatus = (status: ReportGeneratorStatus, t: (key: string) => string) => {
  switch (status) {
    case ReportGeneratorStatus.InDraft:
      return [{ key: 'Publish', label: t('Publish') }, {key: 'Edit', label: t('Edit')}];
    case ReportGeneratorStatus.Published:
      return [{ key: 'View', label: t('View') }];
    case ReportGeneratorStatus.Failed:
      return [];
    case ReportGeneratorStatus.InProgress:
      return [];
    case ReportGeneratorStatus.SentForPublishing:
      return [];
  }
  return [{ key: 'View', label: t('View')}];
};

export const transformDateRangeToReportPayload = (
  dateRange: [Dayjs, Dayjs],
  reportStore: ReportStore
) => {
  const stationRole = reportStore.rootStore.userStore.user.stations.find(station => station.id === reportStore.rootStore.stationStore.currentStation.id);
  return {
    startDate: dateRange[0].startOf('day').format('YYYY-MM-DD'),
    endDate: dateRange[1].endOf('day').format('YYYY-MM-DD'),
    reportType: ReportType.Periodic,
    reportName: 'Periodic Report' + dayjs().format('YYYY-MM-DD HH:mm:ss') + reportStore.rootStore.stationStore.currentStation.name,
    reportCreatedBy: reportStore.rootStore.userStore.user?.firstName + ' ' + reportStore.rootStore.userStore.user?.lastName || 'User',
    reportCreatedByUserId: reportStore.rootStore.userStore.user.id,
    reportCreatedByUserRole: stationRole?.roleName || 'Role',
  };
};

export const canShowThreeDots = (status: ReportGeneratorStatus) => {
  return status === ReportGeneratorStatus.InDraft;
};

